import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Tag, Space, Popconfirm, message, Select, Form, InputNumber, Modal, Switch, Divider, Row, Col, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api-client';
import { LabelWithHelp } from '../../components/ui/HelpTooltip';
import { t } from '../../i18n';

const { Text } = Typography;

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface City {
  id: number;
  regionId: number;
  nameUz: string;
  nameRu: string;
}

interface RoutePricing {
  id: number;
  fromRegionId: number;
  toRegionId: number;
  tripType: string;
  basePrice: number;
  pricePerKm: number;
  minPrice: number;
  maxPrice: number | null;
  economPrice: number | null;
  standardPrice: number | null;
  comfortPrice: number | null;
  businessPrice: number | null;
  sevenPlusPrice: number | null;
  surgeMultiplier: number;
  isActive: boolean;
  fromRegion?: Region;
  toRegion?: Region;
  fromCityId: number | null;
  toCityId: number | null;
  fromCity?: { id: number; nameUz: string; nameRu: string } | null;
  toCity?: { id: number; nameUz: string; nameRu: string } | null;
}

const tripTypeLabels: Record<string, string> = {
  INTERREGION_TAXI: 'Viloyatlar aro taksi',
  SHAHARLAR_ARO: 'Viloyat ichida taksi',
  YOL_YOLAKAY: 'Viloyatlar aro savdolashish',
};

const tripTypeColors: Record<string, string> = {
  INTERREGION_TAXI: 'blue',
  SHAHARLAR_ARO: 'cyan',
  YOL_YOLAKAY: 'green',
};

const currencyFormatter = (value: any) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
const currencyParser = (value: any) => value!.replace(/\s?/g, '');

export const RoutePricingList = () => {
  const { create } = useNavigation();
  const [regions, setRegions] = useState<Region[]>([]);
  const [fromCities, setFromCities] = useState<City[]>([]);
  const [toCities, setToCities] = useState<City[]>([]);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [fromFilterCityId, setFromFilterCityId] = useState<number | undefined>();
  const [toFilterCityId, setToFilterCityId] = useState<number | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RoutePricing | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const tripType = Form.useWatch('tripType', form);

  const { tableProps, tableQueryResult } = useTable<RoutePricing>({
    resource: 'admin/pricing/routes',
    syncWithLocation: true,
  });

  useEffect(() => {
    fetchRegions();
    fetchAllCities();
  }, []);

  const fetchAllCities = async () => {
    try {
      const response = await apiClient.get('/admin/cities');
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setAllCities(data);
    } catch {
      setAllCities([]);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await apiClient.get('/admin/regions');
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setRegions(data);
    } catch {
      message.error('Viloyatlarni yuklashda xatolik');
    }
  };

  const fetchCities = async (regionId: number, target: 'from' | 'to') => {
    try {
      const response = await apiClient.get('/admin/cities', { params: { regionId } });
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      if (target === 'from') setFromCities(data);
      else setToCities(data);
    } catch {
      if (target === 'from') setFromCities([]);
      else setToCities([]);
    }
  };

  const handleCreate = () => {
    setEditingRecord(null);
    setFromCities([]);
    setToCities([]);
    form.resetFields();
    form.setFieldsValue({ isActive: true, surgeMultiplier: 1.0 });
    setIsModalOpen(true);
  };

  const handleEdit = (record: RoutePricing) => {
    setEditingRecord(record);
    form.setFieldsValue({
      fromRegionId: record.fromRegionId,
      toRegionId: record.toRegionId,
      fromCityId: record.fromCityId || undefined,
      toCityId: record.toCityId || undefined,
      tripType: record.tripType,
      basePrice: record.basePrice,
      pricePerKm: record.pricePerKm,
      minPrice: record.minPrice || undefined,
      maxPrice: record.maxPrice || undefined,
      economPrice: record.economPrice || undefined,
      standardPrice: record.standardPrice || undefined,
      comfortPrice: record.comfortPrice || undefined,
      businessPrice: record.businessPrice || undefined,
      sevenPlusPrice: record.sevenPlusPrice || undefined,
      surgeMultiplier: Number(record.surgeMultiplier) || 1.0,
      isActive: record.isActive,
    });
    fetchCities(record.fromRegionId, 'from');
    fetchCities(record.toRegionId, 'to');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/admin/pricing/routes/${id}`);
      message.success('Narx o\'chirildi');
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/pricing/routes/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const payload: any = {
        fromRegionId: values.fromRegionId,
        toRegionId: values.toRegionId,
        fromCityId: values.fromCityId || null,
        toCityId: values.toCityId || null,
        tripType: values.tripType,
        surgeMultiplier: values.surgeMultiplier || 1.0,
        isActive: values.isActive,
      };

      if (values.tripType === 'INTERREGION_TAXI') {
        payload.economPrice = values.economPrice;
        payload.standardPrice = values.standardPrice;
        payload.comfortPrice = values.comfortPrice;
        payload.businessPrice = values.businessPrice;
        payload.sevenPlusPrice = values.sevenPlusPrice;
      } else {
        // SHAHARLAR_ARO or YOL_YOLAKAY — min/max bounds
        payload.minPrice = values.minPrice || 0;
        payload.maxPrice = values.maxPrice || null;
      }

      if (editingRecord) {
        await apiClient.put(`/admin/pricing/routes/${editingRecord.id}`, payload);
        message.success('Narx yangilandi');
      } else {
        await apiClient.post('/admin/pricing/routes', payload);
        message.success('Narx yaratildi');
      }
      setIsModalOpen(false);
      tableQueryResult.refetch();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Xatolik yuz berdi');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const formatShort = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${Math.round(value / 1000)}k`;
    return `${value}`;
  };

  const getSurgeColor = (multiplier: number) => {
    if (multiplier >= 2) return 'red';
    if (multiplier >= 1.5) return 'orange';
    if (multiplier > 1) return 'gold';
    return 'green';
  };

  const columns = [
    {
      title: 'Yo\'nalish',
      key: 'route',
      render: (_: unknown, record: RoutePricing) => {
        const from = record.fromCity?.nameUz || record.fromRegion?.nameUz || `ID: ${record.fromRegionId}`;
        const to = record.toCity?.nameUz || record.toRegion?.nameUz || `ID: ${record.toRegionId}`;
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <Text strong>{from}</Text>
              <SwapRightOutlined style={{ color: '#1ABC9C' }} />
              <Text strong>{to}</Text>
            </Space>
            {(record.fromCity || record.toCity) && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.fromRegion?.nameUz} → {record.toRegion?.nameUz}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t('trips.tripType'),
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag color={tripTypeColors[type] || 'blue'}>{tripTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: t('common.price'),
      key: 'prices',
      render: (_: unknown, record: RoutePricing) => {
        if (record.tripType === 'INTERREGION_TAXI' && record.standardPrice) {
          return (
            <Space size={4} wrap>
              {record.economPrice && <Tag>E: {formatShort(record.economPrice)}</Tag>}
              <Tag color="blue">S: {formatShort(record.standardPrice)}</Tag>
              {record.comfortPrice && <Tag color="cyan">K: {formatShort(record.comfortPrice)}</Tag>}
              {record.businessPrice && <Tag color="purple">B: {formatShort(record.businessPrice)}</Tag>}
              {record.sevenPlusPrice && <Tag color="orange">7+: {formatShort(record.sevenPlusPrice)}</Tag>}
            </Space>
          );
        }
        if (record.tripType === 'SHAHARLAR_ARO' || record.tripType === 'YOL_YOLAKAY') {
          if (!record.minPrice && !record.maxPrice) return <Text type="secondary">Cheklanmagan</Text>;
          return (
            <Text>
              {record.minPrice ? formatShort(record.minPrice) : '0'} — {record.maxPrice ? formatShort(record.maxPrice) : '∞'}
            </Text>
          );
        }
        // Legacy/CARGO fallback
        return <Text>{formatCurrency(record.basePrice)} + {formatCurrency(record.pricePerKm)}/km</Text>;
      },
    },
    {
      title: 'Surge',
      key: 'surgeMultiplier',
      width: 80,
      render: (_: unknown, record: RoutePricing) => {
        const val = Number(record.surgeMultiplier) || 1;
        return (
          <Tag color={getSurgeColor(val)} icon={val > 1 ? <ThunderboltOutlined /> : undefined}>
            x{val.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: t('common.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (active: boolean, record: RoutePricing) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id)}
          checkedChildren={t('common.active')}
          unCheckedChildren={t('common.inactive')}
        />
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: RoutePricing) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="O'chirishni tasdiqlaysizmi?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Determine if we should show tariff prices or min/max bounds
  const showTariffPrices = tripType === 'INTERREGION_TAXI';
  const showMinMax = tripType === 'SHAHARLAR_ARO' || tripType === 'YOL_YOLAKAY';
  const minMaxRequired = tripType === 'YOL_YOLAKAY';

  // Apply pickup/destination city filters client-side. A route matches when its
  // city matches the selected city, or when the route is region-level (no city)
  // and its region contains the selected city.
  const fromFilterRegionId = fromFilterCityId
    ? allCities.find((c) => c.id === fromFilterCityId)?.regionId
    : undefined;
  const toFilterRegionId = toFilterCityId
    ? allCities.find((c) => c.id === toFilterCityId)?.regionId
    : undefined;

  const sourceData = (tableProps.dataSource as RoutePricing[] | undefined) || [];
  const filteredData = sourceData.filter((row) => {
    if (fromFilterCityId) {
      const matchesFrom =
        row.fromCityId === fromFilterCityId ||
        (row.fromCityId == null && row.fromRegionId === fromFilterRegionId);
      if (!matchesFrom) return false;
    }
    if (toFilterCityId) {
      const matchesTo =
        row.toCityId === toFilterCityId ||
        (row.toCityId == null && row.toRegionId === toFilterRegionId);
      if (!matchesTo) return false;
    }
    return true;
  });

  const matchesSide = (
    row: RoutePricing,
    side: 'from' | 'to',
    cityId: number | undefined,
    regionId: number | undefined,
  ) => {
    if (cityId == null) return true;
    if (side === 'from') {
      return (
        row.fromCityId === cityId ||
        (row.fromCityId == null && row.fromRegionId === regionId)
      );
    }
    return (
      row.toCityId === cityId ||
      (row.toCityId == null && row.toRegionId === regionId)
    );
  };

  const buildCityOptions = (side: 'from' | 'to') => {
    // Restrict candidate rows by the OPPOSITE filter so each side's options
    // only contain cities that have at least one matching route given the
    // current selection on the other side.
    const oppositeSide = side === 'from' ? 'to' : 'from';
    const oppositeCityId = side === 'from' ? toFilterCityId : fromFilterCityId;
    const oppositeRegionId = side === 'from' ? toFilterRegionId : fromFilterRegionId;
    const candidateRows = sourceData.filter((row) =>
      matchesSide(row, oppositeSide, oppositeCityId, oppositeRegionId),
    );

    const cityIdField = side === 'from' ? 'fromCityId' : 'toCityId';
    const regionIdField = side === 'from' ? 'fromRegionId' : 'toRegionId';
    return allCities
      .filter((city) =>
        candidateRows.some(
          (row) =>
            row[cityIdField] === city.id ||
            (row[cityIdField] == null && row[regionIdField] === city.regionId),
        ),
      )
      .map((city) => {
        const region = regions.find((r) => r.id === city.regionId);
        return {
          value: city.id,
          label: region ? `${city.nameUz} (${region.nameUz})` : city.nameUz,
        };
      });
  };

  const fromCityOptions = buildCityOptions('from');
  const toCityOptions = buildCityOptions('to');

  const isFiltered = fromFilterCityId != null || toFilterCityId != null;

  return (
    <>
      <List
        title={t('pricing.routesTab')}
        headerButtons={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Yangi narx
          </Button>
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
          <Col xs={24} sm={10}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Qayerdan (shahar)"
              style={{ width: '100%' }}
              value={fromFilterCityId}
              onChange={(val) => setFromFilterCityId(val)}
              options={fromCityOptions}
            />
          </Col>
          <Col xs={24} sm={10}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Qayerga (shahar)"
              style={{ width: '100%' }}
              value={toFilterCityId}
              onChange={(val) => setToFilterCityId(val)}
              options={toCityOptions}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Button
              onClick={() => {
                setFromFilterCityId(undefined);
                setToFilterCityId(undefined);
              }}
              disabled={!isFiltered}
              block
            >
              Tozalash
            </Button>
          </Col>
        </Row>
        <Table
          {...tableProps}
          dataSource={filteredData}
          pagination={
            tableProps.pagination
              ? { ...tableProps.pagination, total: filteredData.length }
              : false
          }
          columns={columns}
          rowKey="id"
        />
      </List>

      <Modal
        title={editingRecord ? 'Narxni tahrirlash' : 'Yangi narx qo\'shish'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isActive: true, surgeMultiplier: 1.0 }}
        >
          {/* ===== ROUTE SELECTION ===== */}
          <Divider orientation="left" plain>Yo'nalish</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fromRegionId"
                label={<LabelWithHelp label="Qayerdan (Viloyat)" help="Yo'lovchi chiqadigan viloyatni tanlang" />}
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    if (val) fetchCities(val, 'from');
                    else setFromCities([]);
                  }}
                >
                  {regions.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.nameUz}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {fromCities.length > 0 && (
                <Form.Item
                  name="fromCityId"
                  label={<Text type="secondary" style={{ fontSize: 12 }}>Shahar (ixtiyoriy)</Text>}
                  style={{ marginTop: -8 }}
                >
                  <Select placeholder="Barcha shaharlar" allowClear size="small">
                    {fromCities.map((city) => (
                      <Select.Option key={city.id} value={city.id}>
                        {city.nameUz}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              <Form.Item
                name="toRegionId"
                label={<LabelWithHelp label="Qayerga (Viloyat)" help="Yo'lovchi boradigan viloyatni tanlang" />}
                rules={[{ required: true, message: 'Viloyatni tanlang' }]}
              >
                <Select
                  placeholder="Viloyatni tanlang"
                  showSearch
                  optionFilterProp="children"
                  onChange={(val) => {
                    if (val) fetchCities(val, 'to');
                    else setToCities([]);
                  }}
                >
                  {regions.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.nameUz}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {toCities.length > 0 && (
                <Form.Item
                  name="toCityId"
                  label={<Text type="secondary" style={{ fontSize: 12 }}>Shahar (ixtiyoriy)</Text>}
                  style={{ marginTop: -8 }}
                >
                  <Select placeholder="Barcha shaharlar" allowClear size="small">
                    {toCities.map((city) => (
                      <Select.Option key={city.id} value={city.id}>
                        {city.nameUz}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item
            name="tripType"
            label="Sayohat turi"
            rules={[{ required: true, message: 'Sayohat turini tanlang' }]}
          >
            <Select placeholder="Sayohat turini tanlang">
              <Select.Option value="INTERREGION_TAXI">Viloyatlar aro taksi</Select.Option>
              <Select.Option value="SHAHARLAR_ARO">Viloyat ichida taksi</Select.Option>
              <Select.Option value="YOL_YOLAKAY">Viloyatlar aro savdolashish</Select.Option>
            </Select>
          </Form.Item>

          {/* ===== TARIFF PRICES (INTERREGION_TAXI only) ===== */}
          {showTariffPrices && (
            <>
              <Divider orientation="left" plain>Tarif narxlari</Divider>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="economPrice"
                    label="Ekonom (so'm)"
                    rules={[{ required: true, message: 'Kiriting' }]}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="85 000" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="standardPrice"
                    label="Standart (so'm)"
                    rules={[{ required: true, message: 'Kiriting' }]}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="100 000" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="comfortPrice"
                    label="Komfort (so'm)"
                    rules={[{ required: true, message: 'Kiriting' }]}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="140 000" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="businessPrice"
                    label="Biznes (so'm)"
                    rules={[{ required: true, message: 'Kiriting' }]}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="200 000" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="sevenPlusPrice"
                    label="7+ o'rinli (so'm)"
                    rules={[{ required: true, message: 'Kiriting' }]}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="160 000" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                noStyle
                dependencies={['economPrice', 'standardPrice', 'comfortPrice', 'businessPrice', 'sevenPlusPrice']}
              >
                {({ getFieldValue }) => {
                  const e = getFieldValue('economPrice');
                  const s = getFieldValue('standardPrice');
                  const c = getFieldValue('comfortPrice');
                  const b = getFieldValue('businessPrice');
                  const sp = getFieldValue('sevenPlusPrice');
                  const prices = [e, s, c, b, sp].filter((v) => v != null);
                  const ordered = prices.every((v, i) => i === 0 || v >= prices[i - 1]);
                  if (prices.length > 1 && !ordered) {
                    return (
                      <div style={{ color: '#ff4d4f', marginBottom: 16 }}>
                        Narxlar tartibda bo'lishi kerak (Ekonom &le; Standart &le; Komfort &le; Biznes &le; 7+)
                      </div>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </>
          )}

          {/* ===== MIN/MAX BOUNDS (SHAHARLAR_ARO & YOL_YOLAKAY) ===== */}
          {showMinMax && (
            <>
              <Divider orientation="left" plain>
                {tripType === 'YOL_YOLAKAY' ? 'Savdolashish chegaralari' : 'Narx chegaralari'}
              </Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="minPrice"
                    label={<LabelWithHelp label="Minimal narx (so'm)" help="Haydovchi/yo'lovchi kirita oladigan eng kam narx" />}
                    rules={minMaxRequired ? [{ required: true, message: 'Minimal narxni kiriting' }] : []}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="50 000" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxPrice"
                    label={<LabelWithHelp label="Maksimal narx (so'm)" help="Haydovchi/yo'lovchi kirita oladigan eng ko'p narx" />}
                    rules={minMaxRequired ? [{ required: true, message: 'Maksimal narxni kiriting' }] : []}
                  >
                    <InputNumber style={{ width: '100%' }} formatter={currencyFormatter} parser={currencyParser} min={0} placeholder="200 000" />
                  </Form.Item>
                </Col>
              </Row>

              {!minMaxRequired && (
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: -8, marginBottom: 16 }}>
                  Agar narx chegaralari kiritilmasa, haydovchi istalgan narx belgilashi mumkin.
                </Text>
              )}
            </>
          )}

          {/* ===== SURGE COEFFICIENT ===== */}
          <Divider orientation="left" plain>
            <ThunderboltOutlined /> Surge koeffitsienti
          </Divider>

          <Form.Item
            name="surgeMultiplier"
            label={<LabelWithHelp label="Surge koeffitsienti" help="Narxni ko'paytiruvchi koeffitsient. 1.0 = oddiy narx, 1.5 = 50% qimmatroq, 2.0 = 2 baravar qimmat" />}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={5}
              step={0.1}
              placeholder="1.0"
              addonAfter={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {form.getFieldValue('surgeMultiplier') > 1
                    ? `+${Math.round((form.getFieldValue('surgeMultiplier') - 1) * 100)}% qo'shimcha`
                    : 'Oddiy narx'}
                </Text>
              }
            />
          </Form.Item>

          <Divider />

          <Form.Item name="isActive" valuePropName="checked">
            <Switch checkedChildren={t('common.active')} unCheckedChildren={t('common.inactive')} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingRecord ? 'Saqlash' : 'Yaratish'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
